import { createSlice } from '@reduxjs/toolkit';
import { getResponseError } from '../../api';
import { RootState } from '../../app/store';
import { findOnDay } from './actions';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import {getName} from '../entries/utils';
import {userDisplayName} from '../users/utils';

interface checksState {
  error: boolean | null;
  isWaiting: boolean;
  entries: EntrySummary[] | null;
  shops: Shop[] | null;
  shopEntries: any[] | null;
  fieldDefinitions: FieldDefinition[] | null,
  fieldDefinitionSummary: FieldDefinitionSummary[] | null;
  fieldDefinitionTotalSummary: Record<string, number> | null;
  roles: {[Key in UserRole]?: User[] } | null;
  message: string;
}

const initialState: checksState = {
  error: false,
  isWaiting: false,
  entries: null,
  shops: null,
  shopEntries: null,
  message: '',
  fieldDefinitions: null,
  fieldDefinitionSummary: null,
  fieldDefinitionTotalSummary: null,
  roles: null,
}

function resetData(state:checksState) {
  state.entries= null;
  state.shops= null;
  state.shopEntries= null;
  state.fieldDefinitions= null;
  state.fieldDefinitionSummary= null;
  state.fieldDefinitionTotalSummary= null;
}

const getFieldFragmentsInFieldDefinitions = (fieldDefinitions:FieldDefinition[]|null) => {
  if (fieldDefinitions === null) { return []; }
  return fieldDefinitions
  .map((fieldDefinition:FieldDefinition) => fieldDefinition.fieldFragments)
  .flat()
  .filter((fieldFragment:FieldFragment) => fieldFragment.inputType === 'number');
};

function pending(state:any, message:string = '') {
  state.error = null;
  state.isWaiting = true;
  state.message = message;
}

function fulfilled(state:any, message:string = '') {
  state.error = false;
  state.isWaiting = false;
  state.message = message;
}

function rejected(state:any, message:string = '') {
  state.error = true;
  state.isWaiting = false;
  state.message = message;
}

export const checksSlice = createSlice({
  name: 'checks',
  initialState,
  reducers: {},
  extraReducers: (builder) => builder
    // GET SINGLE ENTRY
    .addCase(findOnDay.pending, (state) => {
      pending(state);
    })
    .addCase(findOnDay.fulfilled, (state, action) => {
      fulfilled(state);
      const { shops, entries, fieldDefinitionSummary } = action.payload;
      if (shops && entries) {
        state.shops = action.payload.shops || null;
        state.entries = action.payload.entries || null;
        state.fieldDefinitions = action.payload.fieldDefinitions || null;
        state.fieldDefinitionSummary = fieldDefinitionSummary || null;
        state.fieldDefinitionTotalSummary = action.payload.fieldDefinitionTotalSummary || null;
        state.roles = action.payload.roles || null;

        state.shopEntries = shops.map((shop:Shop) => {
          const columns:{key: string, value: number}[] = [
            ...entries
            .filter((entry:EntrySummary) => entry.shop_id === shop.id)
            .map((entry:EntrySummary) => ({ key: entry.fieldFragment_id, value: entry.summary })),
            ...fieldDefinitionSummary
            .filter((entry:FieldDefinitionSummary) => entry.shop.id === shop.id)
            .map((entry:FieldDefinitionSummary) => ({ key: entry.key, value: entry.summary })),
          ];
          const data = new Map(Object.entries({
            id: shop.id,
            key: shop.id,
            name: shop.name,
          }));
          columns.forEach(column => data.set(column.key, column.value));

          return Object.fromEntries(data);
        });
      } else {
        resetData(state);
      }
    })
    .addCase(findOnDay.rejected, (state, action) => {
      rejected(state, getResponseError(action));
      resetData(state);
    })
    .addDefaultCase(() => {}),
});

// SELECTORS
export const selectEntries = (state: RootState) => state.checks.entries;
export const selectShops = (state: RootState) => state.checks.shops;
export const selectShopEntries = (state: RootState) => state.checks.shopEntries || [];
export const selectFieldDefinitions = (state: RootState) => state.checks.fieldDefinitions || [];
export const selectShopEntriesSummary = (state: RootState) => state.checks.fieldDefinitionTotalSummary;
export const selectAvailableColumns = (state: RootState):ColumnDescription[] =>
sortBy(
[
  // list of FieldDefinitions
  ...uniqBy(state.checks.fieldDefinitionSummary, 'fieldDefinition.id'),
  // list of FieldFragments
  ...getFieldFragmentsInFieldDefinitions(state.checks.fieldDefinitions).map(item => ({...item, key: item.id})),
]
, (o) => ( getName(o) || '').toLowerCase() )
  || [];

export const selectAvailableFilters = (state: RootState):ColumnDescription[] => {
  return (state.checks.roles?.MANAGER || []).map((user:User) => ({
    key: user.id,
    name: userDisplayName(user),
    data: user.shops.map(item => item.id),
  }));
}

export const selectFeedback = (state: RootState): Feedback => ({
  isWaiting: state.checks.isWaiting,
  message: Array.isArray(state.checks.message) ? state.checks.message : [state.checks.message],
  error: state.checks.error,
});

export default checksSlice.reducer;
